import React from 'react';

import PrimerBases from 'assembly/PrimerBases';
import Misprime from 'assembly/tabs/primer-assembly/Misprime';
import SecondaryStructureLink from 'assembly/tabs/primer-assembly/SecondaryStructureLink';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ElementMouseEvent} from 'util/Types';

export interface PrimerRowAttrs {
  baseMatchingStatuses: Array<boolean>;
  binding: number;
  dg: number | null;
  fragmentResultColor: string;
  misprime: boolean | null;
  onClickDg: () => void;
  onClickMisprime: () => void;
  onClickPair: () => void;
  onClickPrimer: (e: ElementMouseEvent) => void;
  overhang: number;
  pairId: number;
  pairedId: number;
  primerBases: string;
  primerId: number;
  primerName: string;
  tmAnneal: number;
  tmDiff: number;
  tmWhole: number;
}

export interface AssemblyAttrs {
  finalized: boolean;
  showBinding: boolean;
  showHomology: boolean;
  showPrintableSummary: boolean;
}

export type MaybeEnzyme = {isGoldenGate: true; enzyme: Enzyme} | {isGoldenGate: false; enzyme?: never};

type PrimerRowProps = PrimerRowAttrs & AssemblyAttrs & MaybeEnzyme;

export function PrimerRow({
  baseMatchingStatuses,
  binding,
  dg,
  enzyme,
  finalized,
  fragmentResultColor,
  isGoldenGate,
  misprime,
  onClickDg,
  onClickMisprime,
  onClickPair,
  onClickPrimer,
  overhang,
  primerBases,
  primerName,
  showBinding,
  showHomology,
  showPrintableSummary,
  tmAnneal,
  tmDiff,
  tmWhole,
}: PrimerRowProps): JSX.Element {
  return (
    <tr>
      <td
        style={{[showPrintableSummary ? 'borderLeftColor' : 'backgroundColor']: fragmentResultColor}}
        className="printable-color"
      >
        <a onClick={onClickPrimer} {...getTestProps({component: 'PrimerAssemblyRow', element: 'primerName'})}>
          {primerName}
        </a>
      </td>
      {showPrintableSummary && (
        <td>
          <PrimerBases
            baseMatchingStatuses={baseMatchingStatuses}
            bases={primerBases}
            {...(isGoldenGate ? {isGoldenGate: true, enzyme} : {isGoldenGate: false})}
            overhangLength={overhang}
          />
        </td>
      )}
      <td className="tm text-center" {...getTestProps({component: 'PrimerAssemblyRow', element: 'tmWhole'})}>
        {tmWhole.toFixed(1)}
      </td>
      <td className="tm text-center">{tmAnneal.toFixed(1)}</td>
      <td className="tm text-center">
        <a onClick={onClickPair}>{tmDiff.toFixed(2)}</a>
      </td>
      {showBinding && <td className="binding text-center">{binding}</td>}
      {showHomology && <td className="homology text-center">{overhang}</td>}
      <td className="monomer text-center">
        <SecondaryStructureLink dg={dg} onClick={onClickDg} />
      </td>
      {!finalized && (
        <td className="misprime text-center">
          <Misprime misprime={misprime} onClick={onClickMisprime} />
        </td>
      )}
    </tr>
  );
}

export type PrimerRowsProps = AssemblyAttrs & {
  primerRows: Array<PrimerRowAttrs>;
} & MaybeEnzyme;

export default function PrimerRows({
  enzyme,
  finalized,
  isGoldenGate,
  primerRows,
  showBinding,
  showHomology,
  showPrintableSummary,
}: PrimerRowsProps): JSX.Element {
  return (
    <fieldset>
      <legend>Assembly Primers</legend>
      <div className="row primer-assembly-primers">
        <div className="col col-md-12">
          {showPrintableSummary && isGoldenGate && (
            <p>
              Recognition site in <span className="primer-base-recognition-site">bold</span>. Sticky end{' '}
              <span className="primer-base-sticky-end">underlined</span>. Bases that bind to parent in{' '}
              <span className="primer-base-match">italics</span>.
            </p>
          )}
          <table className="table table-condensed">
            <thead>
              <tr>
                <th className="primer-name">Primer</th>
                {showPrintableSummary && <th>Bases</th>}
                <th className="text-center">
                  T<sub>m</sub>
                  <sup>whole</sup>
                  <br />
                  (&deg;C)
                </th>
                <th className="text-center">
                  T<sub>m</sub>
                  <sup>anneal</sup>
                  <br />
                  (&deg;C)
                </th>
                <th className="text-center">
                  T<sub>m</sub>
                  <sup>diff</sup>
                  <br />
                  (&deg;C)
                </th>
                {showBinding && (
                  <th className="text-center">
                    Binding
                    <br />
                    (bp)
                  </th>
                )}
                {showHomology && (
                  <th className="text-center">
                    Overhang
                    <br />
                    (bp)
                  </th>
                )}
                <th className="text-center">
                  Self &Delta;G
                  <br />
                  (kcal)
                </th>
                {!finalized && <th className="text-center">Mispriming</th>}
              </tr>
            </thead>
            <tbody>
              {primerRows.map(({primerId, ...restPrimerRowProps}) => (
                <PrimerRow
                  key={primerId}
                  finalized={finalized}
                  {...(isGoldenGate ? {isGoldenGate: true, enzyme} : {isGoldenGate: false})}
                  primerId={primerId}
                  showBinding={showBinding}
                  showHomology={showHomology}
                  showPrintableSummary={showPrintableSummary}
                  {...restPrimerRowProps}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </fieldset>
  );
}
